import { Info } from "lucide-react";

import { IgButton, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { Stack } from "@mui/material";

/**
 * The publish bar for the TEMPLATE rule page, which never runs gates and so
 * has no gate strip to carry the action. Code rules merged this bar into
 * `GateProgressStrip` — one surface reporting the verdict and offering the
 * publish — so this component deliberately has a single call site.
 */
export const UnpublishedDraftCallout = ({
  isLive,
  isPublishing,
  onPublish,
  disabledHint,
}: {
  /**
   * Whether the rule is in front of shoppers right now. Only the copy turns on
   * it: a live rule's gap is a storefront that is out of date, while an
   * unattached rule's gap is a change simply waiting for its first deploy.
   */
  isLive: boolean;
  isPublishing: boolean;
  onPublish: () => void;
  /**
   * Why Publish cannot run this moment (a gate run in flight, unsaved edits
   * that would ship the previous source). Present ⇒ the button is disabled and
   * carries this as its tooltip, because a publish that silently does nothing
   * reads as a broken button.
   */
  disabledHint?: string;
}) => (
  <Stack
    aria-live="polite"
    direction="row"
    role="status"
    spacing={2}
    sx={(theme) => ({
      alignItems: "center",
      backgroundColor: theme.igBackgroundColors.tonal.info,
      border: `1px solid ${theme.igBorders.info.default}`,
      borderRadius: 2,
      flexWrap: "wrap",
      gap: 1,
      justifyContent: "space-between",
      px: 2,
      py: 1,
    })}
  >
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: "center", flex: 1, minWidth: 0 }}
    >
      <Info aria-hidden size={18} />
      <IgTypography sx={{ fontWeight: 600 }} variant="body2">
        Draft saved — not published
      </IgTypography>
      <IgTypography color="text.secondary" variant="body2">
        {isLive
          ? "Shoppers still see the published version."
          : "This rule has not been published yet."}
      </IgTypography>
    </Stack>
    <IgTooltip title={disabledHint ?? ""}>
      <span>
        <IgButton
          disabled={isPublishing || disabledHint !== undefined}
          loading={isPublishing}
          size="small"
          variant="contained"
          // Called with NO arguments: the handler the page passes takes
          // optional publish notes, and forwarding onClick's event would hand
          // a MouseEvent to a parameter typed `string | undefined`.
          onClick={() => onPublish()}
        >
          Publish
        </IgButton>
      </span>
    </IgTooltip>
  </Stack>
);
